<template>
  <section class="faqSection">
    <h1 class="faqSectionTitle">FAQ</h1>
    <div class="faqsContainer">
      <div v-for="faq in faqList" :key="faq.question" class="faqCollapsibleWrapper">
        <FaqCollapsible class="faqCollapsible">
          <template #title>
            <span class="faqTitle">{{ faq.question }}</span>
          </template>

          <template #content>
            <span v-dompurify-html="faq.answer" class="faqContent" />
          </template>
        </FaqCollapsible>
      </div>
    </div>
    <RouterLink :to="{ name: 'HelpPages' }" class="btn-round-white faqButton">Learn more</RouterLink>
  </section>
</template>

<script setup>
import FaqCollapsible from 'components/reused-components/faqCollapsible/FaqCollapsible'

const faqList = [
  {
    question: 'How do I start a podcast?',
    answer: `
      <p>
      Record your episode on your phone or in your favorite studio.
      Once you have recorded and produced your show there are just 4 more steps.
      <ol>
        <li>Upload your audio to Afripods</li>
        <li>Use the FREE Afripods audio editor to produce your episode</li>
        <li>Publish and distribute your episode to all outlets using your Afripods RSS feed</li>
        <li>Promote your episode to your audience</li>
      </ol>
      </p>
      <p>
      Starting a podcast is easy, staying consistent is how you will grow and monetize your genius.
      </p>
      `,
  },
  {
    question: 'Is Afripods FREE?',
    answer: `
      Yes it is!
      Afripods is currently offering free podcast hosting for creators to upload and edit their podcasts
      and view their analytics while growing their audience.
      <a href="/creator">Create your account</a> and start uploading your podcast today!
      `,
  },
  {
    question: 'How do I make Money?',
    answer: `
      Afripods is building a monetization platform for hosted podcasts.
      This will be driven by advertising, and the amount earned will be based on the number of listens that a podcast receives.
      We believe that African content creators should get paid for their content and have prioritised it in our technical plans.
      Coming soon!
      `,
  },
  {
    question: 'Can I move my podcast to Afripods?',
    answer: `
      Yes, you can migrate your podcast to be hosted on Afripods in 3 simple steps.
      Get started by clicking <a href="/podcaster/migrate">here</a>.
      `,
  },
  {
    question:
      'If my podcast is on Afripods can it be listened to on Spotify, Apple, Google and other podcast platforms?',
    answer: `
      Yes, once your podcast is uploaded to Afripods and registered on other distribution platforms,
      your audience can listen on their preferred podcast platform(s) anywhere in the world.
      `,
  },
  {
    question: 'What is the difference between Submit your RSS and Migrate to Afripods?',
    answer: `
      When you submit your podcast to Afripods you are adding your RSS feed to our library.
      When you migrate to Afripods you are leaving your current podcast host to enjoy Afripods'
      free service which includes the top online audio editor in the world,
      state of the industry statistics dashboard and top African podcasting community.
      `,
  },
  {
    question: 'How can I get involved with the podcasting community?',
    answer: `
      <p>
      Welcome home.
      We believe it takes a village to build community.
      Podcasters need spaces to connect, ask questions, help others, learn and share ideas.
      </p>
      <p>
      Join our countrywide virtual community meetups across the continent,
      check out our blog posts,
      watch our Instagram lives,
      listen to our Twitter spaces,
      follow us on all social media platforms for updates or email us at <a href="mailto:contactus@afripods.com">contactus@afripods.com</a>.
      </p>
      <p>
      Join us on  all social media platforms:
      </p>

      <div class="social-icons">
        <a class="social-icon-link" href="https://www.instagram.com/afripods" rel="external">
          <span class="sr-only">Instagram</span><i class="fab fa-instagram fa-lg"></i>
        </a>
        <a class="social-icon-link" href="https://www.youtube.com/@afripods" rel="external">
          <span class="sr-only">Youtube</span><i class="fab fa-youtube fa-lg"></i>
        </a>
        <a class="social-icon-link" href="https://www.facebook.com/afripods" rel="external">
          <span class="sr-only">Facebook</span><i class="fab fa-facebook fa-lg"></i>
        </a>
        <a class="social-icon-link" href="https://twitter.com/afripods" rel="external">
          <span class="sr-only">Twitter</span><i class="fab fa-twitter fa-lg"></i>
        </a>
        <a class="social-icon-link" href="https://www.linkedin.com/company/afripods-ab" rel="external">
          <span class="sr-only">LinkedIn</span><i class="fab fa-linkedin fa-lg"></i>
        </a>
      </div>
      `,
  },
  {
    question: 'Can I add my podcast to the Afripods library?',
    answer: `
      Yes of course!
      You can keep your podcast on your current hosting platform and add your show
      to the largest library of African stories on the planet by clicking <a href="https://afripods.africa/podcaster/submit">here</a>.
      `,
  },
  {
    question: 'Can I listen to a podcast in my vernacular language?',
    answer: `
      Yes, Afripods offers the option to listen to podcasts in up to 50 languages on the Afripods platform.
      Podcasts on Afripods have language tags assigned to them for the language in which they are recorded
      and you can search for your language using our search filters.
      `,
  },
  {
    question: 'How do I contact Afripods for partnership,  advertising and press opportunities?',
    answer: `
      For partnership opportunities, please reach out to us at <a href="mailto:contactus@afripods.com">contactus@afripods.com</a>
      `,
  },
]
</script>

<style lang="scss" scoped>
@import 'assets/scss/base/bootstrap_variables';
@import 'assets/scss/base/functions';

.faqSection {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  padding: 0 16px;
  margin: 60px auto;
  color: $color-black;

  @media (min-width: $screen-sm-min) {
    padding: 0 32px;
    margin: 120px auto;
  }

  .faqsContainer {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-evenly;
    margin-top: 32px;

    @media (min-width: $screen-sm-min) {
      margin-top: 54px;
    }

    .faqCollapsibleWrapper {
      display: flex;
      flex-basis: 100%;

      @media (min-width: $screen-sm-min) {
        flex-basis: 50%;
      }

      .faqCollapsible {
        margin-top: 40px;

        .faqTitle {
          font-size: to-rem(22);
          font-weight: 600;
          line-height: 1.2;

          @media (min-width: $screen-sm-min) {
            font-size: to-rem(30);
          }
        }

        .faqContent {
          font-size: to-rem(18);
          font-weight: 400;
          line-height: 1.5;

          @media (min-width: $screen-sm-min) {
            font-size: to-rem(20);
          }
        }
      }
    }
  }

  .faqSectionTitle {
    font-size: to-rem(32);
    font-weight: 400;
    line-height: 1.1;
    color: inherit;

    @media (min-width: $screen-sm-min) {
      font-size: to-rem(52);
    }
  }

  .faqButton {
    margin-top: 60px;

    @media (min-width: $screen-sm-min) {
      margin-top: 120px;
    }
  }
}

:deep(.faqContent) {
  a {
    color: $color-informative-blue;
  }

  ol,
  li {
    list-style: outside;
    list-style-type: decimal;
  }

  .social-icons {
    display: flex;
    column-gap: 10px;
    align-items: flex-end;
    justify-content: start;
    order: 6;
    height: 100%;

    @media (min-width: $screen-md-min) {
      column-gap: clamp(8px, 1.2vw, 16px);
      order: 4;
      margin: 0;
    }

    .social-icon-link {
      display: block;
      font-size: to-rem(60);
      color: $color-black;
      transition: color 0.2s;

      &:hover {
        color: $brand-orange;
      }

      @media (min-width: $screen-md-min) {
        font-size: to-rem(35);
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// FaqSection.vue